/**
 * 经营与定价。
 *
 * 除 PricingPolicy 外全部为空表——旧系统没有付费，迁移也没有交易数据。
 * 页面照样要建：结构先立起来，接支付时不需要再改管理端。
 *
 * 金额一律以分为单位的整数，灵力为整数，禁止浮点。除 100 只在展示层做。
 */

import {
  Alert,
  App,
  Card,
  Collapse,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../services/auth";
import { DataTable } from "../../components/DataTable";
import { POLICY_SECTIONS, describeField } from "./policy-dictionary";
import { fmtCents, fmtDateTime, fmtNumber, RelativeTime, ShortId, StatusTag } from "../../components/format";

export function CommercePage() {
  const [tab, setTab] = useState("policies");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "policies", label: "定价策略", children: <Policies /> },
        { key: "orders", label: "订单", children: <Orders /> },
        { key: "payments", label: "支付", children: <Payments /> },
        { key: "refunds", label: "退款", children: <Refunds /> },
        { key: "ads", label: "激励视频", children: <AdRewards /> },
      ]}
    />
  );
}

interface Policy {
  id: string;
  version: number;
  payload: Record<string, unknown>;
  status: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  publishedBy: string | null;
  note: string | null;
}

function Policies() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["pricingPolicies"],
    queryFn: () => api.get<Policy[]>("/api/v1/admin/commerce/policies"),
  });
  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  const current = data?.find((p) => p.status === "published") ?? data?.[0];

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="定价策略线上可改，自动版本化"
        description="与 CharacterVersion 同构：保存即产生新版本，不原地修改。下单时冻结 policyVersion，后续调价不影响已下订单。运行时读当前生效版本，packages/config/src/economy.ts 降级为类型定义与首次启动的种子值。"
      />

      {current && <PolicyExplainer policy={current} loading={isLoading} />}

      <Card title="版本历史" loading={isLoading} styles={{ body: { padding: 12 } }}>
        <Table<Policy>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={data ?? []}
          expandable={{
            expandedRowRender: (r) => (
              <Collapse
                size="small"
                items={Object.entries(r.payload).map(([k, v]) => ({
                  key: k,
                  label: <Typography.Text code>{k}</Typography.Text>,
                  children: <div className="mono">{JSON.stringify(v, null, 2)}</div>,
                }))}
              />
            ),
          }}
          columns={[
            { title: "版本", dataIndex: "version", width: 90, render: (v: number) => `v${v}` },
            { title: "状态", dataIndex: "status", width: 120, render: (v) => <StatusTag value={v} /> },
            { title: "生效于", dataIndex: "effectiveFrom", width: 170, render: fmtDateTime },
            { title: "失效于", dataIndex: "effectiveTo", width: 170, render: fmtDateTime },
            { title: "发布者", dataIndex: "publishedBy", width: 110, render: (v) => v || "—" },
            { title: "备注", dataIndex: "note", render: (v) => v || "—" },
          ]}
        />
      </Card>
    </Space>
  );
}

/** 把 payload 逐字段翻译成中文。翻译表见 policy-dictionary.ts。 */
function PolicyExplainer({ policy, loading }: { policy: Policy; loading: boolean }) {
  const entries = Object.entries(policy.payload);

  return (
    <Card
      title={`当前生效：v${policy.version} 的中文解读`}
      loading={loading}
      styles={{ body: { padding: 12 } }}
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          只读。改价需要 pricing.publish 权限，且会产生新版本
        </Typography.Text>
      }
    >
      <Collapse
        size="small"
        defaultActiveKey={["skillPowerCost", "grant"]}
        items={entries.map(([section, value]) => {
          const doc = POLICY_SECTIONS[section];
          return {
            key: section,
            label: (
              <Space wrap>
                <Typography.Text strong>{doc?.label ?? section}</Typography.Text>
                <Typography.Text code style={{ fontSize: 11 }}>
                  {section}
                </Typography.Text>
                {doc && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {doc.meaning}
                  </Typography.Text>
                )}
                {!doc && <Tag color="orange">未登记翻译</Tag>}
              </Space>
            ),
            children: <PolicyValue section={section} value={value} />,
          };
        })}
      />
    </Card>
  );
}

function PolicyValue({ section, value }: { section: string; value: unknown }) {
  // 标量：直接一行
  if (value === null || typeof value !== "object") {
    return <Typography.Text code>{String(value)}</Typography.Text>;
  }

  // 数组（充值档位）：转表格
  if (Array.isArray(value)) {
    const keys = [...new Set(value.flatMap((v) => (v && typeof v === "object" ? Object.keys(v) : [])))];
    return (
      <Table
        size="small"
        pagination={false}
        rowKey={(_, i) => String(i)}
        dataSource={value as Record<string, unknown>[]}
        columns={keys.map((k) => {
          const doc = describeField(section, k);
          return {
            title: (
              <Tooltip title={doc?.meaning}>
                <span>
                  {doc?.label || k}
                  {doc?.unit ? <Typography.Text type="secondary">（{doc.unit}）</Typography.Text> : null}
                </span>
              </Tooltip>
            ),
            dataIndex: k,
            render: (v: unknown) =>
              k === "priceCents" && typeof v === "number" ? `${fmtCents(v)}（${v} 分）` : String(v),
          };
        })}
      />
    );
  }

  // 对象：键 → 中文名 → 值
  const rows = Object.entries(value as Record<string, unknown>).map(([k, v]) => {
    const doc = describeField(section, k);
    return { key: k, label: doc?.label || "", meaning: doc?.meaning ?? "", unit: doc?.unit ?? "", value: v };
  });

  return (
    <Table
      size="small"
      pagination={false}
      rowKey="key"
      dataSource={rows}
      columns={[
        {
          title: "字段",
          dataIndex: "key",
          width: 220,
          render: (k: string, r) => (
            <Space direction="vertical" size={0}>
              <Typography.Text code style={{ fontSize: 12 }}>
                {k}
              </Typography.Text>
              {r.label && <Typography.Text strong>{r.label}</Typography.Text>}
            </Space>
          ),
        },
        {
          title: "值",
          dataIndex: "value",
          width: 160,
          render: (v: unknown, r) => (
            <Typography.Text strong>
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
              {r.unit ? <Typography.Text type="secondary">{` ${r.unit}`}</Typography.Text> : null}
            </Typography.Text>
          ),
        },
        {
          title: "说明",
          dataIndex: "meaning",
          render: (v: string) =>
            v ? (
              <Typography.Text type="secondary">{v}</Typography.Text>
            ) : (
              <Tag color="orange">未登记翻译</Tag>
            ),
        },
      ]}
    />
  );
}

function Orders() {
  return (
    <DataTable
      path="/api/v1/admin/commerce/orders"
      columns={[
        { title: "订单", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
        {
          title: "用户",
          dataIndex: "userName",
          width: 140,
          render: (v: string | null, r: { userId: string }) => <Link to={`/users/${r.userId}`}>{v || "—"}</Link>,
        },
        { title: "类型", dataIndex: "itemType", width: 130 },
        { title: "档位", dataIndex: "tierKey", width: 110, render: (v) => v || "—" },
        { title: "金额", dataIndex: "priceCents", width: 100, align: "right", render: fmtCents },
        { title: "到账灵力", dataIndex: "grantedPower", width: 100, align: "right", render: fmtNumber },
        { title: "状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
        { title: "渠道", dataIndex: "payChannel", width: 100, render: (v) => v || "—" },
        { title: "虚拟支付", dataIndex: "virtualPayment", width: 210, render: (v: { environment: string; lastErrorCode: string | null; refundReviewRequired: boolean } | null) => v ? (
          <Space direction="vertical" size={2}>
            <Tag>{v.environment === "sandbox" ? "沙箱" : "现网"}</Tag>
            {v.refundReviewRequired ? <Tag color="red">退款后灵力回收待复核</Tag> : null}
            {v.lastErrorCode ? <Typography.Text type="warning">{v.lastErrorCode}</Typography.Text> : null}
          </Space>
        ) : "—" },
        { title: "宿主", dataIndex: "hostKind", width: 120, render: (v) => v || "—" },
        { title: "策略版本", dataIndex: "policyVersion", width: 90, align: "right", render: (v) => `v${v}` },
        { title: "商户单号", dataIndex: "outTradeNo", width: 200 },
        { title: "创建", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
      ]}
    />
  );
}

function Payments() {
  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <PaymentControl />
      <DataTable
        path="/api/v1/admin/commerce/payments"
        columns={[
          { title: "支付", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
          { title: "订单", dataIndex: "orderId", width: 90, render: (v) => <ShortId value={v} /> },
          { title: "渠道", dataIndex: "provider", width: 130 },
          { title: "渠道流水号", dataIndex: "providerTransactionNo", width: 180, render: (v) => v || "—" },
          { title: "金额", dataIndex: "amountCents", width: 100, align: "right", render: fmtCents },
          { title: "状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
          { title: "失败码", dataIndex: "failureCode", width: 130, render: (v) => v || "—" },
          { title: "支付时间", dataIndex: "paidAt", width: 170, render: fmtDateTime },
        ]}
      />
    </Space>
  );
}

interface PaymentControlStatus {
  enabled: boolean;
  acceptingNewPayments: boolean;
  canEnable: boolean;
  paymentMode: "development_stub" | "wechat_virtual";
  virtualPaymentEnvironment: "production" | "sandbox";
  coinPerYuan: number;
  requirements: {
    channelReady: boolean;
    miniappAppIdConfigured: boolean;
    wechatPayCredentialsConfigured: boolean;
    pricingPolicyActive: boolean;
    coinRatioMatches: boolean;
  };
  activePolicyVersion: number | null;
  updatedAt: string | null;
}

function PaymentControl() {
  const { can } = useAuth();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ confirmation: string; reason: string }>();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const query = useQuery({
    queryKey: ["paymentControl"],
    queryFn: () =>
      api.get<PaymentControlStatus>("/api/v1/admin/commerce/payment-control"),
  });
  const mutation = useMutation({
    mutationFn: (input: {
      enabled: boolean;
      confirmation: string;
      reason: string;
      expectedUpdatedAt: string | null;
    }) =>
      api.patch<PaymentControlStatus>(
        "/api/v1/admin/commerce/payment-control",
        input,
      ),
    onSuccess: (data) => queryClient.setQueryData(["paymentControl"], data),
  });

  const status = query.data;
  const openConfirmation = (enabled: boolean) => {
    form.resetFields();
    setPendingEnabled(enabled);
  };
  const closeConfirmation = () => {
    if (!mutation.isPending) setPendingEnabled(null);
  };
  const submit = async () => {
    if (pendingEnabled === null || !status) return;
    try {
      const values = await form.validateFields();
      await mutation.mutateAsync({
        enabled: pendingEnabled,
        confirmation: values.confirmation,
        reason: values.reason,
        expectedUpdatedAt: status.updatedAt,
      });
      message.success(pendingEnabled ? "微信支付已开启" : "微信支付已关闭");
      setPendingEnabled(null);
    } catch (cause) {
      // 表单校验会自行标红；只有服务端/网络错误需要额外 toast。
      if (cause instanceof Error) message.error(cause.message);
    }
  };

  if (query.error) {
    return <Alert type="error" showIcon message={(query.error as Error).message} />;
  }

  const canToggle = can("commerce.payment.toggle");
  const enablingBlocked = status ? !status.enabled && !status.canEnable : true;

  return (
    <>
      <Card
        title="微信支付总开关"
        loading={query.isLoading}
        extra={status ? (
          <Space>
            {!canToggle ? <Tag>只读</Tag> : null}
            <Switch
              checked={status.enabled}
              checkedChildren="已开启"
              unCheckedChildren="已关闭"
              disabled={!canToggle || mutation.isPending || enablingBlocked}
              onChange={openConfirmation}
            />
          </Space>
        ) : null}
      >
        {status ? (
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Alert
              type={status.acceptingNewPayments ? "success" : status.enabled ? "error" : "info"}
              showIcon
              message={
                status.acceptingNewPayments
                  ? "正在接受新的微信支付订单"
                  : status.enabled
                    ? "开关已开启，但前置条件不完整；用户端仍不会开放充值"
                    : "新的支付订单已关闭，用户端会隐藏整组充值卡片"
              }
              description="关闭只阻止创建新订单；在途订单的微信回调、到账履约、历史查询与退款不会被中断。"
            />
            <Space wrap>
              <RequirementTag ready={status.requirements.channelReady} label="支付通道" />
              <RequirementTag
                ready={status.requirements.pricingPolicyActive}
                label={status.activePolicyVersion ? `定价策略 v${status.activePolicyVersion}` : "定价策略"}
              />
              {status.paymentMode === "development_stub" ? (
                <Tag color="blue">开发支付桩</Tag>
              ) : (
                <>
                  <RequirementTag
                    ready={status.requirements.miniappAppIdConfigured}
                    label="小程序 AppID"
                  />
                  <RequirementTag
                    ready={status.requirements.wechatPayCredentialsConfigured}
                    label="虚拟支付凭据"
                  />
                  <RequirementTag ready={status.requirements.coinRatioMatches} label="代币比例与档位一致" />
                  <Tag color={status.virtualPaymentEnvironment === "sandbox" ? "orange" : "green"}>
                    {status.virtualPaymentEnvironment === "sandbox" ? "虚拟支付沙箱" : "虚拟支付现网"} · 1 元 = {status.coinPerYuan} 灵力
                  </Tag>
                </>
              )}
              <Tag color={status.enabled ? "green" : "default"}>
                运营开关{status.enabled ? "已开启" : "已关闭"}
              </Tag>
            </Space>
            <Typography.Text type="secondary">
              最近变更：{status.updatedAt ? fmtDateTime(status.updatedAt) : "尚未设置"}
            </Typography.Text>
          </Space>
        ) : null}
      </Card>

      <Modal
        title={pendingEnabled ? "开启微信支付" : "关闭微信支付"}
        open={pendingEnabled !== null}
        okText={pendingEnabled ? "确认开启" : "确认关闭"}
        okButtonProps={{ danger: pendingEnabled === false }}
        confirmLoading={mutation.isPending}
        onCancel={closeConfirmation}
        onOk={() => void submit()}
        destroyOnHidden
      >
        <Alert
          type={pendingEnabled ? "warning" : "error"}
          showIcon
          message={
            pendingEnabled
              ? "开启后，符合条件的小程序用户会立即看到充值卡片并可创建真实订单。"
              : "关闭后不再接受新订单，但已经付款的订单仍会继续到账。"
          }
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="操作原因"
            rules={[{ required: true, min: 4, max: 200, message: "请填写至少 4 个字的操作原因" }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="confirmation"
            label={`请输入“${pendingEnabled ? "开启微信支付" : "关闭微信支付"}”确认`}
            rules={[
              { required: true },
              {
                validator: (_, value) =>
                  value === (pendingEnabled ? "开启微信支付" : "关闭微信支付")
                    ? Promise.resolve()
                    : Promise.reject(new Error("确认文字不匹配")),
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function RequirementTag({ ready, label }: { ready: boolean; label: string }) {
  return <Tag color={ready ? "green" : "red"}>{label}{ready ? "就绪" : "未就绪"}</Tag>;
}

function Refunds() {
  return (
    <DataTable
      path="/api/v1/admin/commerce/refunds"
      columns={[
        { title: "退款", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
        { title: "订单", dataIndex: "orderId", width: 90, render: (v) => <ShortId value={v} /> },
        { title: "金额", dataIndex: "amountCents", width: 100, align: "right", render: fmtCents },
        { title: "扣回灵力", dataIndex: "refundedPower", width: 100, align: "right", render: fmtNumber },
        { title: "原因", dataIndex: "reason" },
        { title: "状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
        { title: "操作人", dataIndex: "operatorId", width: 110, render: (v) => v || "—" },
        { title: "完成", dataIndex: "completedAt", width: 170, render: fmtDateTime },
      ]}
    />
  );
}

function AdRewards() {
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        激励视频每次 2 灵力，每日上限 3 次。`providerTransId` 唯一，防止重复发放。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/commerce/ad-rewards"
        columns={[
          { title: "记录", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
          {
            title: "关系",
            dataIndex: "relationshipId",
            width: 100,
            render: (v: string) => <Link to={`/users/${v}`}>{v.slice(-8)}</Link>,
          },
          { title: "渠道", dataIndex: "provider", width: 130 },
          { title: "渠道流水", dataIndex: "providerTransId", width: 180, render: (v) => v || "—" },
          { title: "发放灵力", dataIndex: "grantedPower", width: 100, align: "right" },
          { title: "拒绝原因", dataIndex: "rejectedReason", render: (v) => v || "—" },
          { title: "校验时间", dataIndex: "verifiedAt", width: 170, render: fmtDateTime },
        ]}
      />
    </>
  );
}
